import type {
  CodeableConcept,
  CompositionSection,
  Practitioner,
} from '@medplum/fhirtypes'
import {
  codeFromCodeableConcept,
  displayFromCodeableConcept,
  systemFromCodeableConcept,
} from '@seen/core/codeableConcept'
import * as codeSystems from '@seen/core/codeSystems'
import type { ExtendedResourceConstructor } from '@seen/core/ExtendedResource'
import { invariant } from '@seen/core/invariant'
import type { Assessment } from '@seen/core/models/assessments/assessment'
import { NoteLabel } from '@seen/core/models/noteLabel'
import { ValidatedTool } from '@seen/core/models/validatedTool'
import { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import { isVisitNoteCategoryToken } from '@seen/core/models/visitNoteCategory'
import { parseReferenceString } from '@seen/core/utils'
import { Button, IconCalendar } from '@seen/ui'
import { IconLock, IconLockOpen } from '@tabler/icons-react'
import type { DefaultError } from '@tanstack/react-query'
import type { Editor } from '@tiptap/core'
import {
  type JSX,
  type MutableRefObject,
  type ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'
import { CompositionActivity } from '#src/components/activity/CompositionActivity.tsx'
import { CodeableConceptComboboxButton } from '#src/components/CodeableConceptComboboxButton.tsx'
import { Card } from '#src/components/core/Card.tsx'
import { DropdownMenuDeleteItem } from '#src/components/core/dropdown-menu/DropdownMenuDeleteItem.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { H1Editor } from '#src/components/core/H1Editor.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { ScrollArea } from '#src/components/core/scroll-area/index.ts'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { DropdownMenuMedplumLinkItem } from '#src/components/DropdownMenuMedplumLinkItem.tsx'
import { MoreDropdownMenu } from '#src/components/MoreDropdownMenu.tsx'
import { PractitionerNameplate } from '#src/components/nameplates/PractitionerNameplate.tsx'
import { NoteLabelComboboxButton } from '#src/components/NoteLabelComboboxButton.tsx'
import { OwnerRequiredComboboxButton } from '#src/components/OwnerRequiredComboboxButton.tsx'
import { RequiredAnswersDialog } from '#src/components/rich-text-editor/required/RequiredAnswersDialog.tsx'
import { editorRequiredGaps } from '#src/components/rich-text-editor/required/requiredGaps.ts'
import {
  holdsAnswers,
  snapshotNoteCaptures,
  useNoteCaptureWriter,
} from '#src/components/rich-text-editor/required/useNoteCaptureWriter.ts'
import { useRequiredAnswers } from '#src/components/rich-text-editor/required/useRequiredAnswers.ts'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { ValidatedToolForm } from '#src/components/validated-tool-form/ValidatedToolForm.tsx'
import { LayoutModeContext } from '#src/contexts/LayoutModeContext.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'

export interface GenericAssessmentPageComponentProps<T extends Assessment> {
  assessmentId: string
  resourceClass: ExtendedResourceConstructor<T>
  typeOptions: Record<string, CodeableConcept>
  /**
   * Offer the inline note-label + category picker in the header. Opt-in per
   * surface so it lands on assessments but not other compositions sharing this
   * component (incidents).
   */
  showInlineLabels?: boolean
  /**
   * Singular noun for this surface (e.g. `incident`). When provided, the
   * overflow menu offers a soft-delete action and the noun fills the
   * confirmation/toast copy. Opt-in per surface so delete lands on incidents
   * but not assessments.
   */
  resourceNoun?: string
}

export function GenericAssessmentPageComponent<T extends Assessment>({
  assessmentId,
  resourceClass,
  typeOptions,
  showInlineLabels = false,
  resourceNoun,
}: GenericAssessmentPageComponentProps<T>): ReactNode {
  const practitioner = useCurrentPractitioner()
  const isReadOnly = useIsReadOnly()
  const navigate = useNavigate()

  const { data: assessment } = useRead(resourceClass, assessmentId)

  const { isPending: isPatchingAssessment, mutateAsync: patchAssessmentAsync } =
    usePatchMutation<T>()

  // One editor per narrative section; answers in them are held until the
  // assessment is signed, then recorded for its participant.
  const sectionEditors = useRef(
    new Map<number, MutableRefObject<Editor | null>>()
  )
  const editorRefFor = (index: number): MutableRefObject<Editor | null> => {
    const existing = sectionEditors.current.get(index)
    if (existing) {
      return existing
    }
    const created: MutableRefObject<Editor | null> = { current: null }
    sectionEditors.current.set(index, created)
    return created
  }
  const liveEditors = useCallback(
    () =>
      [...sectionEditors.current.values()].flatMap(({ current }) =>
        current && !current.isDestroyed ? [current] : []
      ),
    []
  )
  const getFirstEditor = useCallback(
    () => liveEditors().find((editor) => editorRequiredGaps(editor).length),
    [liveEditors]
  )
  const required = useRequiredAnswers({
    getEditor: getFirstEditor,
    surface: 'assessment',
    action: 'Sign',
  })
  const writeCaptures = useNoteCaptureWriter()

  const addSignature = useCallback(async () => {
    invariant(assessment, 'Assessment is required')
    const editors = liveEditors()
    const gaps = editors.flatMap(editorRequiredGaps)
    if (gaps.length && (await required.check(gaps)) === 'answer') {
      return
    }
    const captures = editors.map(snapshotNoteCaptures).filter(holdsAnswers)
    await patchAssessmentAsync({
      instance: assessment,
      mutateFn: (a) => {
        a.addPractitionerSignature(practitioner.reference)
      },
    })
    if (captures.length) {
      await writeCaptures(captures, {
        noteKey: assessment.reference.reference ?? '',
        subject: assessment.subject,
      }).catch((error: DefaultError) => {
        toast.error(
          `The assessment was signed, but its answers were not recorded${
            error instanceof Error ? `: ${error.message}` : ''
          }`
        )
      })
    }
  }, [
    assessment,
    liveEditors,
    patchAssessmentAsync,
    practitioner.reference,
    required,
    writeCaptures,
  ])

  const [unlocked, setUnlocked] = useState(false)

  const setLayoutMode = useContext(LayoutModeContext)
  useLayoutEffect(() => {
    setLayoutMode?.('full')
    return () => setLayoutMode?.('auto')
  }, [setLayoutMode])

  if (!assessment) {
    return null
  }

  const editable = !assessment.signedOn || unlocked
  const canUnlockAssessment =
    !assessment.signer ||
    practitioner.reference.reference === assessment.signer.reference

  // Same picker the notes use: one combobox over labels (meta.tag) + categories
  // (Composition.category). `skipVersionCheck` makes this a targeted patch that
  // never touches sections/attester, so a signed assessment stays signed.
  const inlineLabelControl = showInlineLabels ? (
    <ResourceFieldForm
      instance={assessment}
      valueFn={(a) => [...a.labels, ...a.categories]}
      patchFn={(a, value) => {
        const next = value as string[]
        a.labels = next.filter(NoteLabel.isValidReferenceString)
        a.categories = next.filter(isVisitNoteCategoryToken)
      }}
      skipVersionCheck
      render={({ field }) => (
        <NoteLabelComboboxButton {...field} includeCategories />
      )}
    />
  ) : null

  return (
    <div className="flex h-full flex-col border-t">
      {/* Header bar */}
      <div className="flex items-center justify-between border-b px-pagex-default py-2.5 lg:px-pagex-lg">
        {editable ? (
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 border-x border-transparent px-4 text-sm">
              <IconCalendar />
              <HoverDate
                date={assessment.startsAt}
                tooltipPrefix="Created on"
              />
            </div>
            <ResourceFieldForm
              instance={assessment}
              valueFn={(a) => {
                invariant(a.author.reference, 'Author is required')
                return a.author.reference
              }}
              patchFn={(a, author) => {
                const authorReference =
                  parseReferenceString<Practitioner>(author)
                invariant(authorReference, 'Author is required')
                a.author = authorReference
              }}
              render={({ field }) => <OwnerRequiredComboboxButton {...field} />}
            />
            <ResourceFieldForm
              instance={assessment}
              valueFn={(a) => a.type}
              patchFn={(a, type) => {
                a.type = type
              }}
              render={({ field }) => (
                <CodeableConceptComboboxButton
                  options={Object.values(typeOptions)}
                  {...field}
                />
              )}
            />
            {inlineLabelControl}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2 py-2.5">
            <div className="flex items-center gap-2 border-x border-transparent px-4 text-sm">
              <IconCalendar />
              <HoverDate
                date={assessment.startsAt}
                tooltipPrefix="Created on"
              />
            </div>
            {assessment.author.reference && (
              <div className="flex items-center border-x border-transparent px-4 text-sm">
                <PractitionerNameplate
                  practitioner={assessment.author.reference}
                  size={5}
                />
              </div>
            )}
            {displayFromCodeableConcept(assessment.type) && (
              <div className="flex items-center border-x border-transparent px-4 text-sm">
                {displayFromCodeableConcept(assessment.type)}
              </div>
            )}
            {assessment.signedOn && (
              <div className="border-x border-transparent px-4 text-sm text-foreground-secondary">
                Signed <HoverDate date={assessment.signedOn} />
              </div>
            )}
            {inlineLabelControl}
          </div>
        )}
        <div className="flex items-center gap-2">
          {!isReadOnly && !assessment.signedOn && (
            <Button
              disabled={isPatchingAssessment}
              onClick={() => {
                void addSignature().then(() => setUnlocked(false))
              }}
            >
              Sign
            </Button>
          )}
          <RequiredAnswersDialog {...required.dialog} />
          {!isReadOnly &&
            !!assessment.signedOn &&
            (canUnlockAssessment ? (
              <Button
                variant="outline"
                onClick={() => setUnlocked((locked) => !locked)}
                size="icon"
              >
                {unlocked ? (
                  <IconLockOpen className="size-4" />
                ) : (
                  <IconLock className="size-4" />
                )}
              </Button>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button variant="outline" disabled size="icon">
                      <IconLock className="size-4" />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="center">
                  Only the practitioner who signed this assessment can unlock
                  it.
                </TooltipContent>
              </Tooltip>
            ))}
          <MoreDropdownMenu>
            {!isReadOnly && resourceNoun && (
              <DropdownMenuDeleteItem
                deleteConfirmationProps={{
                  title: `Delete "${assessment.title}"?`,
                  description: `Deleted ${resourceNoun}s can still be recovered by contacting our team.`,
                  onConfirm: () => {
                    // Navigate only once the soft-delete succeeds, so a failed
                    // patch leaves the user on the detail page to retry.
                    patchAssessmentAsync({
                      instance: assessment,
                      mutateFn: (a) => {
                        a.status = 'entered-in-error'
                      },
                    })
                      .then(() => {
                        toast.success(`"${assessment.title}" deleted`)
                        void navigate(-1)
                      })
                      .catch((error: DefaultError) => {
                        toast.error(
                          `Failed to delete "${assessment.title}": ${error.message}`
                        )
                      })
                  },
                }}
              />
            )}
            <DropdownMenuMedplumLinkItem to={assessment} />
          </MoreDropdownMenu>
        </div>
      </div>
      {/* Scrollable content */}
      <ScrollArea className="flex-1" scrollRestorationId={null}>
        <div className="flex flex-col gap-10 px-pagex-default pt-6 pb-16 lg:px-pagex-lg">
          {editable ? (
            <ResourceFieldForm
              instance={assessment}
              valueFn={(a) => a.title}
              patchFn={(a, title) => {
                a.title = title
              }}
              debounceTyping
              render={({ field }) => (
                <H1Editor placeholder="Title" {...field} />
              )}
              valueSchema={z
                .string()
                .min(1, { message: 'Title must be at least 1 character' })}
            />
          ) : (
            <h1>{assessment.title}</h1>
          )}
          {assessment.sections.map((section, idx) =>
            systemFromCodeableConcept(section.code) ===
            codeSystems.ASSESSMENT_SECTION_VALIDATED_TOOL ? (
              <ValidatedToolSection
                key={section.id ?? `${assessment.template?.reference}-${idx}`}
                assessment={assessment}
                section={section}
                index={idx}
                disabled={!editable}
              />
            ) : (
              <ResourceFieldForm
                key={section.id ?? `${assessment.template?.reference}-${idx}`}
                instance={assessment}
                valueFn={(a) => a.sections[idx].text?.div ?? ''}
                patchFn={(a, html) => {
                  a.setNthSectionText(idx, html)
                }}
                debounceTyping
                render={({ field }) => (
                  <RichTextEditor
                    editorRef={editorRefFor(idx)}
                    observationRecordStatus="deferred"
                    enableColumns
                    contentClassName="p-10"
                    editable={editable}
                    related={[assessment.reference]}
                    {...field}
                  />
                )}
              />
            )
          )}
          <div className="print:hidden">
            <CompositionActivity
              composition={assessment}
              resourceNoun="assessment"
            />
          </div>
        </div>
      </ScrollArea>
    </div>
  )
}

function ValidatedToolSection({
  assessment,
  section,
  index,
  disabled,
}: {
  assessment: Assessment
  section: CompositionSection
  index: number
  disabled: boolean
}): JSX.Element {
  const canonical = codeFromCodeableConcept(section.code)
  const validatedTool = ValidatedTool.buildAll().find(
    (tool) => tool.canonical === canonical
  )
  invariant(validatedTool, `No validated tool found for ${canonical}`)

  const createResponseMutation = useTransactionMutation()

  const { data: response } = useRead(
    ValidatedToolResponse,
    section.entry?.[0]?.reference
  )

  return (
    <Card className="p-10">
      <ValidatedToolForm
        name={validatedTool.name.toLowerCase()}
        response={response}
        onSubmit={(response) => {
          createResponseMutation.mutate((transaction) => {
            const responseRef = transaction.create(response)
            transaction.patch(assessment, (a) => {
              a.sections[index] = {
                ...a.sections[index],
                entry: [responseRef],
              }
            })
          })
        }}
        disabled={disabled}
        restorationId={`${section.id ?? `${assessment.template?.reference}-${index}`}-${validatedTool.name.toLowerCase()}`}
      />
    </Card>
  )
}
